// Single parts line row — display + edit/delete kebab.
// Phase 3.5: mode-aware actual read, manual_price_locked is the only authority,
// "Reapply Matrix" action when locked, GP-warning badge when below threshold.
// Phase 5C: capture confidence icon + inline quick-edit affordance.
import { useEffect, useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { MoreVertical, Pencil, Trash2, RefreshCw, AlertTriangle, SlidersHorizontal } from 'lucide-react';
import {
  suggestedSellHybrid,
  readActualSell,
  round2,
} from '@/lib/parts-financial/calculator';
import { useAuth } from '@/contexts/AuthContext';
import { useFeatureFlag, useExposureFlag } from '@/hooks/useFeatureFlags';
import { useShopPricingConfig, useShopPricingSettings } from '@/hooks/pricing/useShopPricingConfig';
import { useReapplyMatrixPricing } from '@/hooks/pricing/useReapplyMatrixPricing';
import { toast } from '@/hooks/use-toast';
import { cn } from '@/lib/utils';
import { PartsStatusSelect, statusLabel } from './PartsStatusSelect';
import { VendorSourceBadge } from '@/components/tickets/parts-assist/VendorSourceBadge';
import { CaptureMetadataPanel } from '@/components/tickets/parts-assist/CaptureMetadataPanel';
import {
  CaptureConfidenceBadge,
  inferCaptureConfidence,
  type CaptureConfidence,
} from '@/components/tickets/parts-assist/CaptureConfidenceBadge';
import { PartsLineRowQuickEdit } from './PartsLineRowQuickEdit';
import { ProcurementStatusPill } from '@/components/purchasing/ProcurementStatusPill';
import { LinkedInventoryStrip } from './LinkedInventoryStrip';
import type { JobOption } from './PartsLineFormDialog';
import type { PartsLine, PartsLineStatus } from '@/hooks/parts/usePartsLines';
import type { PartsLinePoContext } from '@/hooks/parts/usePartsLinesPoContext';
import { Link } from 'react-router-dom';

const fmt = (n: number) =>
  n.toLocaleString(undefined, { style: 'currency', currency: 'USD', maximumFractionDigits: 2 });

const SOURCE_LABEL: Record<string, string> = {
  manual: 'Manual',
  matrix: 'Matrix',
  optiguard: 'OptiGuard',
  flat_rate: 'Flat',
  imported: 'Imported',
  other: 'Other',
};

interface Props {
  line: PartsLine;
  onEdit: () => void;
  onDelete: () => void;
  onStatusChange: (next: PartsLineStatus) => void;
  canMutate: boolean;
  /** Phase 5C — passed in so quick-edit can re-assign the part's job. */
  jobOptions?: JobOption[];
  /** Phase 7 R5 — PO header info for the linked PO line, if any. */
  poContext?: PartsLinePoContext | null;
}

export function PartsLineRow({ line, onEdit, onDelete, onStatusChange, canMutate, jobOptions = [], poContext = null }: Props) {
  const { activeShop } = useAuth();
  const matrixEnabled = useFeatureFlag('partsPricingMatrix');
  const inventoryEnabled = useFeatureFlag('inventoryEnabled');
  const hintsEnabled = useExposureFlag('ui_partsPricingHints');
  const partsAssistEnabled = useExposureFlag('ui_partsAssist');
  const [quickEditOpen, setQuickEditOpen] = useState(false);
  const rowRef = useRef<HTMLDivElement>(null);
  const [highlight, setHighlight] = useState(false);

  // Phase 5B: source_* columns are populated by the intake modal but not in
  // the typed PartsLine interface yet — read defensively.
  const src = line as unknown as {
    source_vendor?: string | null;
    source_captured_at?: string | null;
    source_availability?: string | null;
    source_external_id?: string | null;
    source_payload?: Record<string, unknown> | null;
  };
  const partsAssistVendor = partsAssistEnabled ? src.source_vendor ?? null : null;
  const explicitConfidence =
    (src.source_payload && (src.source_payload as any).capture_confidence) || null;
  const confidence: CaptureConfidence | null = partsAssistVendor
    ? inferCaptureConfidence({
        source_vendor: partsAssistVendor,
        unit_cost: line.unit_cost == null ? null : Number(line.unit_cost),
        part_number: line.vendor_part_number,
        explicit: explicitConfidence as CaptureConfidence | null,
      })
    : null;

  const { config } = useShopPricingConfig(matrixEnabled ? activeShop?.id : undefined);
  const settingsQ = useShopPricingSettings(activeShop?.id);
  const reapply = useReapplyMatrixPricing(activeShop?.id);

  const qty = Number(line.quantity);
  const cost = line.unit_cost == null ? 0 : Number(line.unit_cost);
  const minGpWarn = Number(settingsQ.data?.min_gp_warning_pct ?? 30);
  const shopMode = settingsQ.data?.matrix_quantity_mode === 'grouped' ? 'grouped' : 'per_unit';

  // Phase 3.5 mode-aware read of authoritative actual sell.
  // DMC-I: legacy `unitSell` arg dropped — `actual_sell_each` is the sole
  // per-unit read authority (Phase 3.5 enforce-authority trigger guarantees
  // it is populated whenever the deprecated `unit_sell` mirror column is).
  const actual = readActualSell({
    quantity: qty,
    pricingMethod: line.pricing_method,
    actualSellEach: line.actual_sell_each,
    actualSellTotal: line.actual_sell_total,
  });

  // Live suggestion (current matrix; never overwrites actual when locked).
  const hybrid = matrixEnabled && cost > 0
    ? suggestedSellHybrid(cost, line.category, config, { quantity: qty, quantityMode: shopMode })
    : null;

  const effectiveEach = actual.each ?? hybrid?.suggestedEach ?? 0;
  const effectiveTotal = actual.total ?? hybrid?.suggestedTotal ?? round2(effectiveEach * qty);
  const gpDollars = round2((effectiveEach - cost) * qty);
  const gpPercent = effectiveEach > 0 ? round2(((effectiveEach - cost) / effectiveEach) * 100) : 0;
  const coreLine = round2(qty * Number(line.core_charge ?? 0));

  const isLocked = !!line.manual_price_locked;
  const sourceLabel = isLocked ? 'Manual Override' : SOURCE_LABEL[line.pricing_source] ?? 'Suggested';
  const showSuggestedAside = isLocked && hybrid && hybrid.suggestedEach > 0
    && Math.abs(hybrid.suggestedEach - effectiveEach) > 0.005;

  const belowGp = cost > 0 && gpPercent < minGpWarn;

  const handleReapply = async () => {
    try {
      await reapply.mutateAsync({ line });
      toast({ title: 'Matrix price re-applied' });
    } catch (e: any) {
      toast({ title: 'Reapply failed', description: e?.message, variant: 'destructive' });
    }
  };

  // Phase 5C — listen for "review existing" navigation from intake modal.
  useEffect(() => {
    const onReview = (e: Event) => {
      const detail = (e as CustomEvent).detail as { lineId?: string } | undefined;
      if (!detail || detail.lineId !== line.id) return;
      rowRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      setHighlight(true);
      const t = setTimeout(() => setHighlight(false), 2200);
      return () => clearTimeout(t);
    };
    window.addEventListener('optibay:parts-assist:review-line', onReview);
    return () => window.removeEventListener('optibay:parts-assist:review-line', onReview);
  }, [line.id]);

  return (
    <div
      ref={rowRef}
      className={cn(
        'grid grid-cols-12 items-center gap-2 rounded-md border bg-card p-3 text-sm transition-colors',
        highlight && 'ring-2 ring-warning bg-warning/5',
      )}
    >
      <div className="col-span-4 min-w-0">
        <div className="flex items-center gap-1.5 min-w-0 flex-wrap">
          <span className="font-medium truncate">{line.description}</span>
          {partsAssistVendor && (
            <VendorSourceBadge
              sourceVendor={partsAssistVendor}
              sourceLabel={line.vendor_name}
              capturedAt={src.source_captured_at}
            />
          )}
          {confidence && <CaptureConfidenceBadge confidence={confidence} compact />}
          {/* Phase 8 R3 — Informational origin badge for canned-job-applied lines. */}
          {line.source_type === 'canned_job' && (
            <Badge variant="outline" className="text-[10px] py-0 h-4" title="Inserted by a canned job template — pricing follows the standard pipeline.">
              From canned job
            </Badge>
          )}
          {/* Phase 7 R5: procurement state always visible (incl. Not Ordered). */}
          <ProcurementStatusPill status={line.procurement_status} showNotOrdered />
        </div>
        <div className="text-xs text-muted-foreground truncate">
          {[line.vendor_name, line.vendor_part_number].filter(Boolean).join(' · ') || '—'}
        </div>
        {/* Phase 7 R5 — Mandatory PO# + vendor metadata strip when linked to a PO. */}
        {poContext && (
          <div className="text-[11px] text-muted-foreground mt-0.5 truncate">
            <Link
              to={`/purchasing/${poContext.purchase_order_id}`}
              className="font-mono hover:underline text-foreground/80"
              onClick={(e) => e.stopPropagation()}
            >
              {poContext.po_number}
            </Link>
            {poContext.vendor_name && <> · {poContext.vendor_name}</>}
          </div>
        )}
        {(line.availability_text || line.eta_text) && (
          <div className="text-[11px] text-muted-foreground mt-0.5 truncate">
            {[line.availability_text, line.eta_text].filter(Boolean).join(' · ')}
          </div>
        )}
        {/* Phase 9 R4 — Render strip for any linked line so existing inventory
            truth (avail/reserved/consumed) stays visible even if the entitlement
            is later disabled. The strip itself gates mutating controls on
            `inventoryEnabled`. */}
        {line.inventory_item_id && (
          <LinkedInventoryStrip
            line={line}
            canMutate={canMutate}
            inventoryEnabled={inventoryEnabled}
          />
        )}
        {partsAssistVendor && (
          <CaptureMetadataPanel
            sourceVendor={partsAssistVendor}
            sourceLabel={line.vendor_name}
            capturedAt={src.source_captured_at}
            availability={src.source_availability}
            externalId={src.source_external_id}
          />
        )}
      </div>
      <div className="col-span-1 text-right tabular-nums">{qty}</div>
      <div className="col-span-1 text-right tabular-nums text-muted-foreground">
        {line.unit_cost != null ? fmt(Number(line.unit_cost)) : '—'}
      </div>
      <div className="col-span-1 text-right tabular-nums">
        <div>{fmt(effectiveEach)}</div>
        {hintsEnabled && cost > 0 && (
          <Badge
            variant={isLocked ? 'secondary' : 'outline'}
            className="text-[9px] py-0 h-3.5 mt-0.5"
            title={
              isLocked
                ? `Manual override${hybrid ? ` · current suggested ${fmt(hybrid.suggestedEach)}` : ''}`
                : `Source: ${sourceLabel}${hybrid?.clampedBy ? ` · clamped (${hybrid.clampedBy})` : ''}`
            }
          >
            {sourceLabel}
          </Badge>
        )}
        {showSuggestedAside && (
          <div className="text-[9px] text-muted-foreground mt-0.5">
            sug {fmt(hybrid!.suggestedEach)}
          </div>
        )}
      </div>
      <div className="col-span-2 text-right tabular-nums">
        <div className="font-medium">{fmt(effectiveTotal + coreLine)}</div>
        <div className="text-[10px] text-muted-foreground flex items-center justify-end gap-1">
          {belowGp && <AlertTriangle className="h-3 w-3 text-warning" aria-label="Below GP threshold" />}
          GP {fmt(gpDollars)} · {gpPercent.toFixed(0)}%
        </div>
      </div>
      <div className="col-span-2">
        {canMutate ? (
          <PartsStatusSelect value={line.status} onChange={onStatusChange} />
        ) : (
          <span className="text-xs text-muted-foreground">{statusLabel(line.status)}</span>
        )}
      </div>
      <div className="col-span-1 flex justify-end gap-0.5">
        {canMutate && (
          <>
            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7"
              onClick={() => setQuickEditOpen((v) => !v)}
              title="Quick edit qty / cost / job"
            >
              <SlidersHorizontal className="h-3.5 w-3.5" />
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" className="h-7 w-7">
                  <MoreVertical className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={onEdit}>
                  <Pencil className="h-3.5 w-3.5 mr-2" /> Edit
                </DropdownMenuItem>
                {isLocked && hybrid && hybrid.suggestedEach > 0 && (
                  <DropdownMenuItem onClick={handleReapply} disabled={reapply.isPending}>
                    <RefreshCw className="h-3.5 w-3.5 mr-2" /> Reapply matrix price
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem onClick={onDelete} className="text-destructive">
                  <Trash2 className="h-3.5 w-3.5 mr-2" /> Delete
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </>
        )}
      </div>
      {quickEditOpen && canMutate && (
        <PartsLineRowQuickEdit
          line={line}
          jobOptions={jobOptions}
          onClose={() => setQuickEditOpen(false)}
        />
      )}
    </div>
  );
}
